import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { getWebBuzzFeed, getWebBuzzLiveRefresh } from '../lib/home';
import type { BuzzFeedV1Response, BuzzLiveRefreshResponse } from '../types';

export const WELCOME_BUZZ_RAIL_MAX_PAGES = 3;

export const welcomeBuzzRailKeys = {
  all: ['welcome-buzz-rail'] as const,
  feed: () => [...welcomeBuzzRailKeys.all, 'feed'] as const,
  live: () => [...welcomeBuzzRailKeys.all, 'live'] as const,
};

function isScreenVisible() {
  if (typeof document === 'undefined') {
    return true;
  }

  return document.visibilityState === 'visible';
}

export function useWelcomeBuzzRail(enabled: boolean = true) {
  const feedQuery = useInfiniteQuery<BuzzFeedV1Response>({
    queryKey: welcomeBuzzRailKeys.feed(),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      getWebBuzzFeed({
        view: 'for_you',
        scope: 'LOCAL',
        cursor: typeof pageParam === 'string' ? pageParam : null,
        signal,
        telemetry: {
          trigger: pageParam ? 'pagination' : 'initial',
          screenVisible: isScreenVisible(),
        },
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo?.nextCursor || undefined,
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  });

  const liveQuery = useQuery<BuzzLiveRefreshResponse>({
    queryKey: welcomeBuzzRailKeys.live(),
    queryFn: ({ signal }) =>
      getWebBuzzLiveRefresh({
        scope: 'LOCAL',
        limit: 12,
        signal,
        telemetry: {
          trigger: 'welcome_rail',
          screenVisible: isScreenVisible(),
        },
      }),
    staleTime: 60 * 1000,
    refetchInterval: enabled ? 60 * 1000 : false,
    refetchIntervalInBackground: false,
    retry: false,
    enabled,
  });

  return {
    feedQuery,
    liveQuery,
  };
}
